import React, {useContext, useState} from "react";
import PropTypes from "prop-types";
import Select from "../../../widget/form/controls/select/Select";
import FormControl from "@material-ui/core/FormControl";
import {CurrencyContext} from "../../../../store/providers/CurrencyProvider";
import {makeStyles} from "@material-ui/styles";
import {getCurrencyEntries} from "../../../../selectors/currencySelect";
import clsx from "clsx";
import ArrowDown1 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/arrows-diagrams/arrows/arrow-down-1.svg";
import ArrowUp1 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/arrows-diagrams/arrows/arrow-up-1.svg";
import Popover from "@material-ui/core/Popover";
import {Box, Typography} from "@material-ui/core";
import MenuList from "@material-ui/core/MenuList";
import MenuItem from "@material-ui/core/MenuItem";
import {Divider} from "@material-ui/core";
import {Button} from "@material-ui/core";
import StreamlineIcon from "../../../widget/streamline-icon/StreamlineIcon";
import Check from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/form-validation/check-1.svg";
import ArrowRight from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/arrows-diagrams/arrows/arrow-right-1.svg";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import {useI18N} from "../../../../common/common";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import {useAnalytics} from "../../../../store/providers/AnalyticsProvider";


const useStyles = makeStyles(theme => ({
    select: {
        height: theme.spacing(4),
        background: theme.palette.grey["0"],
    },
    selectPaddingTop: {
        paddingTop: theme.spacing(2.5),
        "&:focus": {
            background: "unset !important"
        }
    },
    ml1: {
        marginLeft: theme.spacing(1.2)
    },
    ml2: {
        marginLeft: theme.spacing(2)
    },
    popover: {
        minWidth: "240px",
        borderRadius: theme.spacing(1),
    },
    menuItem: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.08)"
        }
    },
    button: {
        "&:hover": {
            backgroundColor: theme.palette.secondary["100"]
        },
    },
    buttonText: {
        color: theme.palette.secondary["900"],
    },
    arrowIcon: {
        color: theme.palette.secondary["900"],
        width: theme.spacing(1.5),
        marginTop: theme.spacing(0.75)
    },
    iconSizeLarge: {
        width: theme.spacing(1.5),
        marginTop: theme.spacing(0.75),
        "& > *:first-child": {
            fontSize: theme.spacing(2.25),
            color: theme.palette.text.white,
        },
    },
    arrowIconRight: {
        position: "absolute",
        right: theme.spacing(-1),
        top: theme.spacing(1),
        [theme.breakpoints.down("xs")]: {
            top: theme.spacing(2),
        }
    },
    checkmarkIcon: {
        marginLeft: "auto"
    },
    preselectedItem: {
        backgroundColor: "rgba(0, 0, 0, 0.08)"
    },
    currencyIndicator: {
        marginLeft: theme.spacing(-1)
    },
    colorWhite: {
        color: theme.palette.text.white
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(2),
        top: theme.spacing(0.5),
        color: theme.palette.grey[500],
    }
}));

const CurrencySelect = ({type, formControlClassName, handleSelection, hideDividers, showSelectedIndicator, noColorHover, textColor, isFooter, location}) => {
    const [currencyState, currencyDispatch] = useContext(CurrencyContext);
    const {sendCustomEvent, eventNames} = useAnalytics();
    const {currencies, currency, currencyNames, currencyName} = currencyState;
    const [anchorEl, setAnchorEl] = useState(null);
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const {i18n} = useI18N();

    const handleCurrencySelect = (name, value) => {
        currencyDispatch({type: "SET_CURRENCY", payload: value});
        handleGA4LanguageChangeTracking(value);
        setOpen(false);

        if (type === "popover") {
            handleClosePopover();
        }

        if (handleSelection) {
            handleSelection(value);
        }
    };

    const handleOpenPopover = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleGA4LanguageChangeTracking = (value) => {
        sendCustomEvent({
            event_name: eventNames.CHANGE_CURRENCY,
            [eventNames.CHANGE_CURRENCY]: {
                currency: value,
                navigation_type: location
            }
        });
    };

    return (
        <>
            {type === "formControl" && currencyState.currency &&
                <FormControl
                    className={clsx(formControlClassName, classes.input)}
                    variant={"outlined"}
                >
                    <Select
                        name={"selectCurrency"}
                        className={classes.select}
                        rootClassName={classes.selectPaddingTop}
                        entries={getCurrencyEntries(currencyState.currencies)}
                        onChange={handleCurrencySelect}
                        value={currencyState.currency}
                    />
                </FormControl>
            }

            {type === "popover" &&
                <>
                    <Button
                        size={"large"}
                        className={noColorHover || classes.button}
                        variant={"text"}
                        onClick={handleOpenPopover}
                        endIcon={
                            <>
                                {anchorEl ? (
                                    <StreamlineIcon
                                        className={isFooter ? classes.iconSizeLarge : classes.arrowIcon}
                                    >
                                        <ArrowUp1 />
                                    </StreamlineIcon>
                                ) : (
                                    <StreamlineIcon
                                        className={isFooter ? classes.iconSizeLarge : classes.arrowIcon}
                                    >
                                        <ArrowDown1 />
                                    </StreamlineIcon>
                                )}
                            </>
                        }
                    >
                        <Typography
                            className={textColor || classes.buttonText}
                            variant="subtitle2"
                            component="div"
                        >
                            {currency}
                        </Typography>
                    </Button>

                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleClosePopover}
                        keepMounted
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                        classes={{paper: classes.popover}}
                    >
                        <MenuList>
                            {currencies && getCurrencyEntries(currencies).map((option, index) => {
                                return (
                                    <div key={index}>
                                        <MenuItem
                                            className={classes.menuItem}
                                            onClick={() => handleCurrencySelect("selectCountry", option.value)}
                                        >
                                            <Typography
                                                variant={"body1"}
                                                className={classes.ml1}
                                            >
                                                {option.label}
                                            </Typography>
                                        </MenuItem>

                                        {index !== (getCurrencyEntries(currencies).length - 1) &&
                                            <Divider />
                                        }
                                    </div>
                                );
                            })}
                        </MenuList>
                    </Popover>
                </>
            }

            {type === "list" &&
                <MenuList>
                    {currencies && getCurrencyEntries(currencies).map((option, index) => {
                        return (
                            <div key={index}>
                                <MenuItem
                                    className={clsx(classes.menuItem, {[classes.preselectedItem]: (showSelectedIndicator && currency === option.value)})}
                                    onClick={() => handleCurrencySelect("selectCountry", option.value)}
                                >
                                    <Typography
                                        className={classes.buttonText}
                                        variant={"subtitle2"}
                                    >
                                        {option.label}
                                    </Typography>

                                    <Typography
                                        variant={"body1"}
                                        className={classes.ml1}
                                    >
                                        {currencyNames[option?.label]}
                                    </Typography>

                                    {showSelectedIndicator && currency === option.value &&
                                        <StreamlineIcon
                                            className={classes.checkmarkIcon}
                                            size={20}
                                        >
                                            <Check />
                                        </StreamlineIcon>
                                    }
                                </MenuItem>

                                {index !== (getCurrencyEntries(currencies).length - 1) && !hideDividers &&
                                    <Divider />
                                }
                            </div>
                        );
                    })}
                </MenuList>
            }
            {type === "listOverlay" &&
                <>
                    <MenuItem onClick={handleClickOpen}>
                        <Typography
                            className={clsx(classes.currencyIndicator, classes.colorWhite)}
                            variant="subtitle2"
                        >
                            {currency}
                        </Typography>

                        <Typography
                            className={clsx(classes.ml2, classes.colorWhite)}
                            variant="body1"
                        >
                            {currencyName}
                        </Typography>

                        <StreamlineIcon
                            className={clsx(classes.arrowIconRight, classes.colorWhite)}
                            size={16}
                        >
                            <ArrowRight />
                        </StreamlineIcon>
                    </MenuItem>
                    <Dialog
                        fullScreen
                        open={open}
                        onClose={handleClose}
                    >
                        <MuiDialogTitle
                            id="customized-dialog-title"
                            onClose={handleClose}
                        >
                            <Box>
                                <Typography
                                    variant={"h5"}
                                    className={classes.ml2}
                                >
                                    {i18n("view.header.profile_menu.currency_selection")}
                                </Typography>
                            </Box>

                            <IconButton
                                aria-label="close"
                                className={classes.closeButton}
                                onClick={handleClose}
                            >
                                <CloseIcon />
                            </IconButton>
                        </MuiDialogTitle>

                        <MenuList>
                            {currencies && getCurrencyEntries(currencies).map((option, index) => {
                                return (
                                    <div key={index}>
                                        <MenuItem
                                            className={clsx(classes.menuItem, {[classes.preselectedItem]: (showSelectedIndicator && currency === option.value)})}
                                            onClick={() => handleCurrencySelect("selectCountry", option.value)}
                                        >
                                            <Typography
                                                className={classes.buttonText}
                                                variant={"subtitle2"}
                                            >
                                                {option.label}
                                            </Typography>

                                            <Typography
                                                variant={"body1"}
                                                className={classes.ml1}
                                            >
                                                {currencyNames[option?.label]}
                                            </Typography>

                                            {showSelectedIndicator && currency === option.value &&
                                                <StreamlineIcon
                                                    className={classes.checkmarkIcon}
                                                    size={20}
                                                >
                                                    <Check />
                                                </StreamlineIcon>
                                            }
                                        </MenuItem>

                                        {index !== (getCurrencyEntries(currencies).length - 1) && !hideDividers &&
                                            <Divider />
                                        }
                                    </div>
                                );
                            })}
                        </MenuList>
                    </Dialog>
                </>
            }
        </>
    );
};

CurrencySelect.propTypes = {
    type: PropTypes.oneOf(["formControl", "popover", "list", "listOverlay"]),
    formControlClassName: PropTypes.string,
    hideDividers: PropTypes.bool,
    handleSelection: PropTypes.func,
    showSelectedIndicator: PropTypes.bool,
    noColorHover: PropTypes.string,
    textColor: PropTypes.string,
    isFooter: PropTypes.bool,
    location: PropTypes.string,
};

export default CurrencySelect;
